/**
 * src/lib/print.ts — branded "Print / Save as PDF" for web reports (web ergonomics).
 *
 * The web app is where owners run reporting; this turns a structured report into a clean,
 * branded printable document (header with the Beam mark, sections, key/value blocks, tables)
 * and triggers the browser's print dialog — from which the user can save a PDF or print to
 * paper. Rendered via a hidden iframe so it never disturbs the app and isn't blocked like a
 * popup. No-op on native (printSupported() is false; callers hide the button there).
 *
 * This module lives in src/lib (outside the app style-guard scope), so the brand colors/fonts
 * here are intentionally inline in the print stylesheet — they must match src/theme tokens.
 *
 * NEVER imports @smartbox/* (mobile invariant 1).
 */
import { Platform } from 'react-native';

export type PrintBlock =
  | { kind: 'keyvalue'; rows: { label: string; value: string; strong?: boolean }[] }
  | { kind: 'table'; columns: string[]; rows: (string | number)[][]; align?: ('left' | 'right')[] }
  | { kind: 'note'; text: string };

export interface PrintSection {
  heading?: string;
  block: PrintBlock;
}

export interface PrintReport {
  title: string;
  subtitle?: string;
  org?: string;
  sections: PrintSection[];
}

export function printSupported(): boolean {
  return Platform.OS === 'web' && typeof window !== 'undefined' && typeof document !== 'undefined';
}

// Brand tokens mirrored for the print stylesheet (must match src/theme/colors.ts).
const C = {
  indigo: '#1B2A6B',
  copper: '#C26B3C',
  ink: '#1B2347',
  muted: '#6B6F86',
  line: '#E4E1D7',
  paper: '#FCFBF8',
};

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

const MARK_SVG = `<svg width="28" height="28" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="beam"><path d="M6 20H20" stroke="${C.ink}" stroke-width="2.6" stroke-linecap="round"/><path d="M20 20H34" stroke="${C.copper}" stroke-width="2.6" stroke-linecap="round"/><circle cx="20" cy="20" r="7" stroke="${C.ink}" stroke-width="2.4"/></svg>`;

function renderBlock(block: PrintBlock): string {
  if (block.kind === 'note') return `<p class="note">${esc(block.text)}</p>`;
  if (block.kind === 'keyvalue') {
    return `<table class="kv">${block.rows
      .map(
        (r) =>
          `<tr class="${r.strong ? 'strong' : ''}"><td class="k">${esc(r.label)}</td><td class="v">${esc(r.value)}</td></tr>`,
      )
      .join('')}</table>`;
  }
  // table
  const align = block.align ?? [];
  const head = block.columns.map((c, i) => `<th class="${align[i] === 'right' ? 'r' : ''}">${esc(c)}</th>`).join('');
  const body = block.rows
    .map(
      (row) =>
        `<tr>${row
          .map((cell, i) => `<td class="${align[i] === 'right' ? 'r' : ''}">${esc(String(cell))}</td>`)
          .join('')}</tr>`,
    )
    .join('');
  return `<table class="data"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
}

function buildHtml(report: PrintReport): string {
  const when = new Date().toLocaleString();
  const sections = report.sections
    .map((s) => `<section>${s.heading ? `<h2>${esc(s.heading)}</h2>` : ''}${renderBlock(s.block)}</section>`)
    .join('');
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(report.title)}</title>
<style>
  :root { color-scheme: light; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', Roboto, sans-serif; color: ${C.ink}; margin: 0; padding: 32px 36px; background: #fff; }
  header { display: flex; align-items: center; justify-content: space-between; border-bottom: 2px solid ${C.indigo}; padding-bottom: 14px; margin-bottom: 22px; }
  .brand { display: flex; align-items: center; gap: 10px; }
  .brand .word { font-weight: 700; font-size: 20px; letter-spacing: 0.3px; }
  .doc-title { font-size: 22px; font-weight: 700; margin: 18px 0 2px; }
  .doc-sub { color: ${C.muted}; font-size: 13px; margin: 0; }
  .meta { color: ${C.muted}; font-size: 11px; text-align: right; }
  h2 { font-size: 13px; text-transform: uppercase; letter-spacing: 1px; color: ${C.muted}; margin: 22px 0 8px; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  table.kv td { padding: 6px 0; border-bottom: 1px solid ${C.line}; }
  table.kv td.k { color: ${C.muted}; }
  table.kv td.v { text-align: right; font-variant-numeric: tabular-nums; }
  table.kv tr.strong td { font-weight: 700; font-size: 15px; border-bottom: none; padding-top: 10px; }
  table.data th { text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.6px; color: ${C.muted}; border-bottom: 1px solid ${C.line}; padding: 6px 8px; }
  table.data td { padding: 6px 8px; border-bottom: 1px solid ${C.line}; font-variant-numeric: tabular-nums; }
  .r { text-align: right; }
  .note { color: ${C.muted}; font-size: 12px; }
  footer { margin-top: 28px; border-top: 1px solid ${C.line}; padding-top: 8px; color: ${C.muted}; font-size: 10px; }
  @media print { body { padding: 0; } @page { margin: 16mm; } }
</style></head>
<body>
  <header>
    <div class="brand">${MARK_SVG}<span class="word">beam</span></div>
    <div class="meta">${report.org ? esc(report.org) + '<br>' : ''}Generated ${esc(when)}</div>
  </header>
  <h1 class="doc-title">${esc(report.title)}</h1>
  ${report.subtitle ? `<p class="doc-sub">${esc(report.subtitle)}</p>` : ''}
  ${sections}
  <footer>Generated by Beam · getabeam.com</footer>
</body></html>`;
}

/** Render `report` to a branded printable document and open the print dialog. No-op on native. */
export function printReport(report: PrintReport): void {
  if (!printSupported()) return;
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);

  const cleanup = () => {
    setTimeout(() => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    }, 1000);
  };

  const doc = iframe.contentWindow?.document;
  if (!doc) {
    cleanup();
    return;
  }
  doc.open();
  doc.write(buildHtml(report));
  doc.close();

  const win = iframe.contentWindow;
  if (!win) {
    cleanup();
    return;
  }
  // Give the iframe a tick to lay out before printing.
  win.onafterprint = cleanup;
  setTimeout(() => {
    try {
      win.focus();
      win.print();
    } finally {
      // Fallback cleanup if onafterprint never fires (some browsers).
      setTimeout(cleanup, 60_000);
    }
  }, 150);
}
